import React, { useState, useEffect } from "react";
import {
  AiOutlineClose,
  AiOutlineCrown,
  AiOutlineDelete,
  AiOutlineEye,
  AiOutlinePlus,
} from "react-icons/ai";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import CreateGroupExecutive from "./CreateGroupExecutive";
import CreatePositionModal from "./CreatePositionModal";
import { FaUserTie } from "react-icons/fa";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import { useNavigate } from "react-router-dom";

const GroupExecutives = ({ groupId }) => {
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  const [groupMembers, setGroupMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  

  const [groupExecutives, setGroupExecutives] = useState([]);
  const [loadingExecutives, setLoadingExecutives] = useState(false);
  const [executivePositions, setExecutivePositions] = useState([]);

  // Modal states
  const [showAddExecutiveModal, setShowAddExecutiveModal] = useState(false);
  const [showCreatePositionModal, setShowCreatePositionModal] = useState(false);

  // Action states
  const [removingExecutiveId, setRemovingExecutiveId] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMembers, setTotalExecutives] = useState(0);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchGroupExecutives = async () => {
    try {
      setLoadingExecutives(true);
      const response = await requestWithAuth(
        `/groups/group-executives/${groupId}/?search=${debouncedSearch}&page=${currentPage}&size=${pageSize}`,
        "GET"
      );

      let filteredExecutives = response.data;

      setGroupExecutives(filteredExecutives?.results);
      setTotalExecutives(filteredExecutives.count);
      setTotalPages(Math.ceil(filteredExecutives.count / pageSize));
      setLoadingExecutives(false);
    } catch (error) {
      console.error("Failed to fetch group members:", error);
      toast.error("Failed to load group members");
      setLoadingExecutives(false);
    }
  };

  useEffect(() => {
    fetchGroupExecutives();
  }, [debouncedSearch, currentPage, pageSize]);

  const getExecutivePositionColor = (position) => {
    const colors = {
      president: "bg-red-100 text-red-800 border-red-200",
      "vice president": "bg-orange-100 text-orange-800 border-orange-200",
      secretary: "bg-green-100 text-green-800 border-green-200",
      treasurer: "bg-blue-100 text-blue-800 border-blue-200",
      organizer: "bg-purple-100 text-purple-800 border-purple-200",
    };
    return (
      colors[position?.toLowerCase()] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };



  const handleRemoveExecutive = async (executiveId) => {
    const result = await Swal.fire({
      title: "Remove Executive?",
      text: "This person will be removed from executive position",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove!",
    });

    if (result.isConfirmed) {
      try {
        setRemovingExecutiveId(executiveId);
        await requestWithAuth(
          `/groups/group-executive/${executiveId}/`,
          "DELETE"
        );

        setGroupExecutives((prev) =>
          prev.filter((exec) => exec.id !== executiveId)
        );
        toast.success("Executive removed successfully!");
        setRemovingExecutiveId(null);
      } catch (error) {
        console.error("Failed to remove executive:", error);
        toast.error("Failed to remove executive");
        setRemovingExecutiveId(null);
      }
    }
  };

  const fetchExecutivePositions = async () => {
    try {
      const response = await requestWithAuth(`/groups/group-positions/${groupId}?paginated=false`, 'GET');
      setExecutivePositions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch executive positions:", error);
    }
  };

  useEffect(()=>{
    fetchExecutivePositions();
  },[])
  

  return (
    <div>
      {/* Section Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Group Executives
          </h3>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreatePositionModal(true)}
              className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <AiOutlinePlus className="text-lg" />
              Create Position
            </button>
            <button
              onClick={() => setShowAddExecutiveModal(true)}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <AiOutlineCrown className="text-lg" />
              Add Executive
            </button>
          </div>
        </div>
      </div>

      {/* Executives Table */}
      <div className="overflow-x-auto">
        {loadingExecutives ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner />
              <p className="text-gray-600 text-sm">Loading executives...</p>
            </div>
          </div>
        ) : groupExecutives?.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Executive
                </th>
                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Position
                </th>
                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Status
                </th>
                

                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {groupExecutives?.map((executive) => (
                <tr
                  key={executive.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {executive.member}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {executive.member}
                        </p>
                      </div>
                      {executive.is_current && (
                        <div className="flex-shrink-0">
                          <AiOutlineCrown
                            className="text-yellow-500 text-lg"
                            title="Current Executive"
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getExecutivePositionColor(
                        executive.position
                      )}`}
                    >
                      {executive.position}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        executive.is_current
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {executive.is_current ? "Current" : "Previous"}
                    </span>
                  </td>
                  

                  <td className="py-4 px-6">
                    <div className="flex items-center gap-1">
                      
                      <button
                        onClick={() => handleRemoveExecutive(executive.id)}
                        disabled={removingExecutiveId === executive.id}
                        className={`p-2 rounded-lg transition-colors ${
                          removingExecutiveId === executive.id
                            ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                            : "text-red-600 hover:bg-red-50"
                        }`}
                        title="Remove Executive"
                      >
                        {removingExecutiveId === executive.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <AiOutlineDelete className="text-lg" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaUserTie className="text-gray-400 w-12 h-12" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No executives appointed
            </h3>
            <p className="text-gray-500 text-center mb-6 max-w-md">
              Start organizing your group by appointing executives to leadership
              positions.
            </p>
            <button
              onClick={() => setShowAddExecutiveModal(true)}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
            >
              <AiOutlineCrown className="text-lg" />
              Appoint First Executive
            </button>
          </div>
        )}
      </div>

      {/* Add Executive Modal */}
      {showAddExecutiveModal && <CreateGroupExecutive groupId={groupId} fetchGroupExecutives={fetchGroupExecutives} setShowCreatePositionModal={setShowCreatePositionModal} fetchExecutivePositions={fetchExecutivePositions} executivePositions={executivePositions} setShowAddExecutiveModal={setShowAddExecutiveModal}/>}

      {/* Create Position Modal */}
      {showCreatePositionModal && <CreatePositionModal groupId={groupId} setShowCreatePositionModal={setShowCreatePositionModal} fetchExecutivePositions={fetchExecutivePositions} />}
    </div>
  );
};

export default GroupExecutives;
